/* ============
 * Edit Page
 * ============
 *
 * This page edits client.
 */
import { mapGetters, mapMutations } from 'vuex';
import accountService from './../../../../services/account';
import clientService from './../../../../services/client';
import clientGroupService from './../../../../services/client-group';
import clientTransformer from './../../../../transformers/client';
import clientTypeService from './../../../../services/clientType';
import priceFieldService from './../../../../services/priceField';
import chartOfAccountsService from './../../../../services/chartOfAccounts';
import addressTypeService from '../../../../services/addressType';
import addressTransformer from './../../../../transformers/address';
import contactTransformer from './../../../../transformers/contact';
import countryService from './../../../../services/country';
import municipalityService from './../../../../services/municipality';
import store from './../../../../store';
import Forms from './../../../../utils/forms/forms';
import stringFormatMixin from './../../../../mixins/stringFormat';
import clientAddressBrreg from '../../../../components/clientAddressBrreg/clientAddressBrreg.vue';
import sendCatalogue from './../../../../components/sendCatalogue/sendCatalogue.vue';
import siteLanguagesService from './../../../../services/siteLanguages';
import marketplaceCustomerService from './../../../../services/marketplaceCustomer';
import licenseService from './../../../../services/license';
import projectService from './../../../../services/project';
import addLicense from './../../../../components/addLicense/addLicense.vue';

export default {
  data() {
    return {
      formBasicInformation: new Forms({
        clientGroupId: {
          value: '',
          type: 'select',
        },
        typeId: {
          value: '',
          type: 'select',
        },
        firstName: {
          value: '',
          type: 'text',
        },
        lastName: {
          value: '',
          type: 'text',
        },
        organizationNumber: {
          type: 'text',
          value: '',
          attributes: {
            hasButton: true,
            buttonLabel: this.$t('static.clients.organizationNumber'),
            buttonHandler: this.buttonHandler,
            hasButton2: true,
            buttonLabel2: this.$t('button.glnLookup'),
            buttonHandler2: this.buttonHandler2,
          },
        },
        companyName: {
          value: '',
          type: 'text',
        },
        email: {
          value: '',
          type: 'email',
        },
        invoiceSendMethod: {
          value: '',
          type: 'select',
        },
        languageId: {
          value: '',
          type: 'select',
        },
        currencyId: {
          value: '',
          type: 'select',
        },
        projectId: {
          value: '',
          type: 'select',
        },
        externalCustomerNumber: {
          value: '',
          type: 'text',
        },
        orderReference: {
          value: '',
          type: 'text',
        },
        orderNote: {
          value: '',
          type: 'textarea',
        },
        gln: {
          value: '',
          type: 'text',
          attributes: {
            disabled: true,
          },
        },
        glnXKoord: {
          value: '',
          type: 'text',
          attributes: {
            disabled: true,
          },
        },
        glnYKoord: {
          value: '',
          type: 'text',
          attributes: {
            disabled: true,
          },
        },
        billingType: {
          value: '',
          type: 'select',
        },
        automaticBilling: {
          value: '',
          type: 'select',
        },
        billingInterval: {
          value: '',
          type: 'select',
        },
        startBilling: {
          value: '',
          type: 'select',
        },
        priceFieldId: {
          value: '',
          type: 'select',
        },
        receivables_account_id: {
          value: '',
          type: 'select',
        },
        dueDays: {
          value: 14,
          type: 'number',
        },
        pricePercentageDiscount: {
          value: '',
          type: 'number',
        },
        isSubClient: {
          value: 0,
          type: 'switch',
        },
        clientParent: {
          value: '',
          type: 'autocomplete',
          attributes: {
            isSearch: true,
            searchMethod: this.searchMethod,
            loading: true,
          },
        },
        billMainParent: {
          value: 0,
          type: 'checkbox',
        },
        isProducer: {
          value: 0,
          type: 'switch',
        },
        isSupplier: {
          value: 0,
          type: 'switch',
        },
        isActive: {
          value: 1,
          type: 'switch',
        },
        manualProcessOrder: {
          value: 1,
          type: 'switch',
        },
        syncToWoo: {
          value: 1,
          type: 'switch',
        },
      }),
      clientAddressNames: null,
      addressFormErrors: {
        postal: null,
        fields: [],
      },
      contactFormErrors: {
        primary: null,
      },
      currentClient: {},
      loading: false,
      tabIndex: '1',
      fetchedClient: {},
      clientAddresses: [],
      marketplaces: [],
      runOnceforAutoBill: true,
    };
  },

  computed: {
    ...mapGetters({
      clientGroups: 'allClientGroups',
      addressTypes: 'addressTypes',
      currentClientContacts: 'currentClientContacts',
      currentClientAddresses: 'currentClientAddresses',
      types: 'allClientTypes',
      countries: 'allCountries',
      municipalities: 'allMunicipalities',
      priceFields: 'allPriceFields',
      chartOfAccounts: 'allChartOfAccounts',
      account: 'account',
      licenses: 'allLicenses',
      currencies: 'currencies',
      languages: 'languages',
      allProjects: 'allProjects',
    }),

    hasEHFModule() {
      if (this.account.modules.length > 0) {
        return this.account.modules.find(module => module.name === 'EHF');
      }
      return false;
    },

    hasMarketplaceModule() {
      if (this.account.modules.length > 0) {
        return this.account.modules.find(module => module.name === 'Marketplace');
      }
      return false;
    },

    /**
     * Returns the client's updated information.
     *
     * @return {Object} The client.
     */
    client() {
      return Object.assign({},
        this.fetchedClient,
        this.formBasicInformation.data(),
      );
    },

    /**
     * Get the current client type.
     *
     * @return {Object} The type.
     */
    currentType() {
      return this.formBasicInformation.options.typeId.filter(t => (
        t.id.toString() === this.client.typeId.toString()
      ))[0] || {};
    },

    /**
     * Get the current client group.
     *
     * @return {Object} The current group.
     */
    currentGroup() {
      return this.formBasicInformation.options.clientGroupId.filter(g => (
        g.id.toString() === this.client.clientGroupId.toString()
      ))[0] || {};
    },

    /**
     * Peppol / EHF billing 3.0 eligibility (ehf_billing_3_0) for overview tab.
     */
    ehfSupportStatusText() {
      if (!this.client.organizationNumber) {
        return this.$t('static.clients.info.ehfSupport.needsOrgNumber');
      }
      if (this.client.ehfBilling3_0) {
        return this.$t('static.clients.info.ehfSupport.yes');
      }
      return this.$t('static.clients.info.ehfSupport.no');
    },
  },

  watch: {
    'formBasicInformation.isSubClient': function (value) {
      if (value == 1) {
        this.formBasicInformation.showField('billMainParent');
        this.formBasicInformation.showField('clientParent');
      } else {
        this.formBasicInformation.hideField('billMainParent');
        this.formBasicInformation.hideField('clientParent');
      }
    },

    tabIndex(value) {
      this.assignTab(value);
    },

    $route() {
      this.urlCurrentTab();
    },

    'formBasicInformation.automaticBilling': function (automaticBilling) {
      if (automaticBilling !== 'none') {
        let feedData = automaticBilling === 'weekly' ? [
          { id: '1_week', name: this.$t('form.billingInterval.week.options.week1') },
          { id: '2_week', name: this.$t('form.billingInterval.week.options.week2') },
          { id: '3_week', name: this.$t('form.billingInterval.week.options.week3') },
        ] : [
          { id: '1_month', name: this.$t('form.billingInterval.month.options.month1') },
          { id: '2_month', name: this.$t('form.billingInterval.month.options.month2') },
          { id: '3_month', name: this.$t('form.billingInterval.month.options.month3') },
          { id: '4_month', name: this.$t('form.billingInterval.month.options.month4') },
          { id: '5_month', name: this.$t('form.billingInterval.month.options.month5') },
          { id: '6_month', name: this.$t('form.billingInterval.month.options.month6') },
        ];
        this.formBasicInformation.setOptions('billingInterval', feedData);
        if (this.runOnceforAutoBill === false || !this.formBasicInformation.billingInterval) {
          this.formBasicInformation.setFieldValue('billingInterval', feedData[0].id);
        }
        this.formBasicInformation.showField('billingInterval');

        feedData = automaticBilling === 'weekly' ? this.daysInWeek() : this.daysInMonth();
        this.formBasicInformation.setOptions('startBilling', feedData);
        if (this.runOnceforAutoBill === false || !this.formBasicInformation.startBilling) {
          this.formBasicInformation.setFieldValue('startBilling', feedData[0].id);
        }
        this.formBasicInformation.showField('startBilling');
        this.runOnceforAutoBill = false;
      } else {
        this.formBasicInformation.hideField('billingInterval');
        this.formBasicInformation.hideField('startBilling');
        this.formBasicInformation.setFieldValue('automaticBilling', 'none');
      }
    },

    allProjects(allProjects) {
      const options = allProjects.map(project => ({
        id: project.id,
        name: project.name,
      }));
      this.formBasicInformation.setOptions('projectId', [{ id: 0, name: ' ' }, ...options]);
    },

    /**
     * Watches state update to inject on Forms class.
     *
     * @param  {Object} clientGroups    The client groups list.
     */
    clientGroups(clientGroups) {
      this.formBasicInformation.setOptions('clientGroupId', [{ id: '', name: '' }, ...clientGroups.all]);
    },

    /**
     * Watches state update to inject on Forms class.
     *
     * @param  {Object} countries    The countries list.
     */
    types(types) {
      this.formBasicInformation.setOptions('typeId', types.map(type => ({
        id: type.id,
        name: type.name,
      })));
    },

    /**
     * Watches state update to inject on Forms class.
     *
     * @param  {Object} languages    The site languages list.
     */
    languages(value) {
      this.formBasicInformation.setOptions('languageId',
        value.all.map(item => ({
          id: item.id,
          name: item.name,
        })));
    },

    currencies(value) {
      this.formBasicInformation.setOptions('currencyId',
        value.all.map(item => ({
          id: item.id,
          name: item.name,
        })));
    },

    /**
     * Watches the value of the client type.
     */
    'formBasicInformation.typeId': function (typeId) {
      if (typeId.toString() === '1') {
        this.formBasicInformation.hideField('companyName');
        this.formBasicInformation.hideField('organizationNumber');
        if (this.formBasicInformation.isHidden('firstName') && this.formBasicInformation.isHidden('firstName')) {
          this.formBasicInformation.showField('firstName');
          this.formBasicInformation.showField('lastName');
        }

        this.formBasicInformation.clearErrors('companyName');
      }

      if (typeId.toString() !== '1') {
        this.formBasicInformation.hideField('firstName');
        this.formBasicInformation.hideField('lastName');

        this.formBasicInformation.clearErrors('firstName');
        this.formBasicInformation.clearErrors('lastName');

        if (this.formBasicInformation.isHidden('companyName')) {
          this.formBasicInformation.showField('companyName');
          this.formBasicInformation.showField('organizationNumber');
        }
      }
    },

    'formBasicInformation.email': function (email) {
      if (email) {
        const primaryContact = this.currentClientContacts.find(x => x.type === 'primary');
        if (primaryContact) {
          const data = Object.assign({}, primaryContact);
          data.email = email;
          const i = this.currentClientContacts.findIndex(x => x.id === primaryContact.id);
          store.dispatch('updateClientContact', {
            data,
            i,
          });
        }
      }
    },

    'formBasicInformation.invoiceSendMethod': function (sendMethod) {
      if (sendMethod == 'EHF') {
        if (!this.formBasicInformation.organizationNumber) {
          this.$message({
            message: this.$t('static.clients.edit.ehf.organizationNumberRequired'),
            type: 'error',
          });
        }
      }
    },

    priceFields(priceFields) {
      const options = priceFields.all.map(type => ({
        id: type.id,
        name: type.name,
      }));
      options.splice(0, 0, { id: '', name: '' });
      this.formBasicInformation.setOptions('priceFieldId', options);
    },

    chartOfAccounts(chartOfAccounts) {
      if (chartOfAccounts && chartOfAccounts.all && chartOfAccounts.all.length > 0) {
        const options = chartOfAccounts.all.map(account => ({
          id: account.id,
          name: `${account.code} - ${account.name}`,
        }));
        options.splice(0, 0, { id: '', name: '' });
        this.formBasicInformation.setOptions('receivables_account_id', options);
        
        // If client data is already loaded, set the value after options are set
        if (this.fetchedClient && this.fetchedClient.receivables_account_id) {
          this.formBasicInformation.setFieldValue('receivables_account_id', this.fetchedClient.receivables_account_id);
        }
      }
    },

  },

  mounted() {
    this.urlCurrentTab();
    this.assignTab(this.tabIndex);
  },

  created() {
    const invoiceSendMethods = [
      { id: 'EMAIL', name: 'Email' },
    ];
    if (this.hasEHFModule) {
      invoiceSendMethods.push({ id: 'EHF', name: 'EHF' });
    }

    this.formBasicInformation.setOptions('invoiceSendMethod', invoiceSendMethods);
    this.formBasicInformation.hideField('billMainParent');
    this.formBasicInformation.hideField('clientParent');

    this.formBasicInformation.setOptions('billingType', [
      { id: 'single', name: this.$t('form.billingTypes.options.single') },
      { id: 'consolidate', name: this.$t('form.billingTypes.options.consolidate') },
    ]);
    this.formBasicInformation.setFieldValue('billingType', 'single');

    this.formBasicInformation.setOptions('automaticBilling', [
      { id: 'none', name: this.$t('form.automaticBilling.options.none') },
      { id: 'weekly', name: this.$t('form.automaticBilling.options.weekly') },
      { id: 'monthly', name: this.$t('form.automaticBilling.options.monthly') },
    ]);
    this.show();
  },


  methods: {
    ...mapMutations({
      toggleModal: 'TOGGLE_MODAL',
      setModalContent: 'SET_MODAL_CONTENT',
      unsetModalContent: 'UNSET_MODAL_CONTENT',
      setModalSize: 'SET_MODAL_SIZE',
      setModalClass: 'SET_MODAL_CLASS',
      setModalProps: 'SET_MODAL_PROPS',
      setModalButtonLoading: 'SET_MODAL_BUTTON_LOADING',
    }),

    getInitials(client) {
      const name = client.companyName || `${client.firstName || ''} ${client.lastName || ''}`;
      const words = name.trim().split(/\s+/).filter(Boolean);
      if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
      return words[0] ? words[0].slice(0, 2).toUpperCase() : '?';
    },

    getAvatarColor(client) {
      const name = client.companyName || `${client.firstName || ''} ${client.lastName || ''}`;
      const colors = ['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#06B6D4', '#EC4899'];
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }
      return colors[Math.abs(hash) % colors.length];
    },

    checkProjectModule() {
      this.formBasicInformation.hideField('projectId');
      let hasProject = false;
      if (this.account.modules.length > 0) {
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < this.account.modules.length; i++) {
          if (this.account.modules[i].name === 'Project') {
            hasProject = true;
          }
        }
      }
      if (hasProject) {
        this.formBasicInformation.showField('projectId');
      }
    },

    glnOverviewShow(type) {
      if (type === 'gln_number') {
        return this.formBasicInformation.gln !== '';
      }
      if (type === 'gln_x_koord') {
        return this.formBasicInformation.glnXKoord !== '';
      }

      if (type === 'gln_y_koord') {
        return this.formBasicInformation.glnYKoord !== '';
      }
      if (type === 'gln') {
        return this.formBasicInformation.glnYKoord !== '' || this.formBasicInformation.glnXKoord !== '' || this.formBasicInformation.gln !== '';
      }
      return false;
    },

    assignTab(value) {
      let tab = '';
      if (value == '4') {
        tab = '#addresses';
      } else if (value == '3') {
        tab = '#contacts';
      } else if (value == '2') {
        tab = '#basics';
      } else {
        tab = '#overview';
      }
      history.pushState({}, null, `/#${this.$route.path}${tab}`);
    },

    urlCurrentTab() {
      if (this.$router.currentRoute.hash == '#addresses') {
        this.tabIndex = '4';
      } else if (this.$router.currentRoute.hash == '#contacts') {
        this.tabIndex = '3';
      } else if (this.$router.currentRoute.hash == '#basics') {
        this.tabIndex = '2';
      } else {
        this.tabIndex = '1';
      }
    },

    daysInWeek() {
      return [
        { id: 'monday', name: this.$t('form.startBilling.week.options.monday') },
        { id: 'tuesday', name: this.$t('form.startBilling.week.options.tuesday') },
        { id: 'wednesday', name: this.$t('form.startBilling.week.options.wednesday') },
        { id: 'thursday', name: this.$t('form.startBilling.week.options.thursday') },
        { id: 'friday', name: this.$t('form.startBilling.week.options.friday') },
        { id: 'saturday', name: this.$t('form.startBilling.week.options.saturday') },
        { id: 'sunday', name: this.$t('form.startBilling.week.options.sunday') },
      ];
    },

    daysInMonth() {
      return [
        { id: '1', name: `1st ${this.$t('form.startBilling.month.options.day')}` }, { id: '2', name: `2nd ${this.$t('form.startBilling.month.options.day')}` },
        { id: '3', name: `3rd ${this.$t('form.startBilling.month.options.day')}` }, { id: '4', name: `4th ${this.$t('form.startBilling.month.options.day')}` },
        { id: '5', name: `5th ${this.$t('form.startBilling.month.options.day')}` }, { id: '6', name: `6th ${this.$t('form.startBilling.month.options.day')}` },
        { id: '7', name: `7th ${this.$t('form.startBilling.month.options.day')}` }, { id: '8', name: `8th ${this.$t('form.startBilling.month.options.day')}` },
        { id: '9', name: `9th ${this.$t('form.startBilling.month.options.day')}` }, { id: '10', name: `10th ${this.$t('form.startBilling.month.options.day')}` },
        { id: '11', name: `11th ${this.$t('form.startBilling.month.options.day')}` }, { id: '12', name: `12th ${this.$t('form.startBilling.month.options.day')}` },
        { id: '13', name: `13th ${this.$t('form.startBilling.month.options.day')}` }, { id: '14', name: `14th ${this.$t('form.startBilling.month.options.day')}` },
        { id: '15', name: `15th ${this.$t('form.startBilling.month.options.day')}` }, { id: '16', name: `16th ${this.$t('form.startBilling.month.options.day')}` },
        { id: '17', name: `17th ${this.$t('form.startBilling.month.options.day')}` }, { id: '18', name: `18th ${this.$t('form.startBilling.month.options.day')}` },
        { id: '19', name: `19th ${this.$t('form.startBilling.month.options.day')}` }, { id: '20', name: `20th ${this.$t('form.startBilling.month.options.day')}` },
        { id: '21', name: `21st ${this.$t('form.startBilling.month.options.day')}` }, { id: '22', name: `22nd ${this.$t('form.startBilling.month.options.day')}` },
        { id: '23', name: `23rd ${this.$t('form.startBilling.month.options.day')}` }, { id: '24', name: `24th ${this.$t('form.startBilling.month.options.day')}` },
        { id: '25', name: `25th ${this.$t('form.startBilling.month.options.day')}` }, { id: '26', name: `26th ${this.$t('form.startBilling.month.options.day')}` },
        { id: '27', name: `27th ${this.$t('form.startBilling.month.options.day')}` }, { id: '28', name: `28th ${this.$t('form.startBilling.month.options.day')}` },
      ];
    },

    /**
     * Method to update the client.
     */
    update() {
      this.formBasicInformation.loading = true;
      const formData = new FormData();
      this.clearAddressErrors();
      this.clientAddresses.forEach((clientAddress) => {
        const data = addressTransformer.send(clientAddress);
        const formKey = `addresses[${clientAddress.addressType}]`;
        if (clientAddress.addressId) {
          formData.append(`${formKey}[id]`, clientAddress.addressId);
        }
        formData.append(`${formKey}[type]`, clientAddress.addressType);
        Object.keys(data).map((k) => {
          const key = `${formKey}[${k}]`;
          formData.append(key, data[k]);
          return null;
        });
      });

      this.currentClientContacts.forEach((clientContact, index) => {
        const data = contactTransformer.send(clientContact);
        const formKey = `contacts[${index}]`;
        if (clientContact.contactId) {
          formData.append(`${formKey}[id]`, clientContact.contactId);
        }
        clientContact.contactType.forEach((contactType, contactTypeIndex) => {
          formData.append(`${formKey}[type][${contactTypeIndex}]`, contactType);
        });
        Object.keys(data).map((k) => {
          const key = `${formKey}[${k}]`;
          formData.append(key, data[k]);
          return null;
        });
      });

      const client = clientTransformer.send(this.formBasicInformation.data());
      Object.keys(client).map((k) => {
        formData.append(k, client[k]);
        return null;
      });

      clientService.update(this.$route.params.id, formData)
        .then((data) => {
          this.$message({
            message: this.$t('static.clients.edit.success'),
            type: 'success',
          });
          this.postalAddressError = false;
          this.formBasicInformation.loading = false;
          if (data.contacts.length && !this.currentClientContacts.length) {
            this.setCurrentContacts(data.contacts);
          }
          this.setAddressFieldErrors([]);
          this.setContactFieldErrors([]);
        }).catch((errors) => {
          if (errors.system_generated_error) {
            this.$message({
              message: errors.system_generated_error[0],
              type: 'error',
            });
          }
          this.setAddressFieldErrors(errors);
          this.setContactFieldErrors(errors);
          this.formBasicInformation.loading = false;
          this.formBasicInformation.recordErrors(clientTransformer.fetch(errors));

          if (this.formBasicInformation.errors.any()) {
            this.tabIndex = '2';
          } else if ('contacts' in errors) {
            this.tabIndex = '3';
          } else if ('addresses' in errors) {
            this.tabIndex = '4';
          }
        });
    },

    inviteToMarketplace(id) {
      marketplaceCustomerService.invite(this.fetchedClient.id, { partner_id: id })
      .then((data) => {
        console.log(data);
      }).catch(() => {
        this.$message({
          message: this.$t('static.clients.inviteToMarketplaceError'),
          type: 'error',
        });
      });
    },

    /**
     * Fetches the client object for editing.
     */
    show() {
      this.loading = true;
      Promise.all([
        clientService.show(this.$route.params.id),
        clientGroupService.all(),
        clientTypeService.all(),
        addressTypeService.all(),
        siteLanguagesService.all(),
        priceFieldService.all(1),
        chartOfAccountsService.all(),
        countryService.all(),
        municipalityService.all(),
        marketplaceCustomerService.partners(),
        licenseService.all(this.$route.params.id),
        accountService.currencies(),
        accountService.languages(),
        projectService.all(),
      ]).then((data) => {
        this.currentClient = data[0];
        const client = data[0];
        this.fetchedClient = client;
        this.loading = false;
        this.clientAddressNames = ['Postal'];
        this.formBasicInformation.assignData(client);
        
        // Ensure receivables_account_id is set after options are loaded
        // Convert null/undefined to empty string for select field
        if (client.receivables_account_id) {
          this.formBasicInformation.setFieldValue('receivables_account_id', client.receivables_account_id);
        } else {
          this.formBasicInformation.setFieldValue('receivables_account_id', '');
        }
        
        const contacts = client.contacts.length ? client.contacts : [];
        this.setCurrentContacts(contacts);
        this.setCurrentAddresses(client.addresses);
        this.lockedFields(client.billingMethodLocked, client.automateBillingMethodLocked);
        if (client.clientParent != '') {
          this.formBasicInformation.setFieldValue('isSubClient', 1);
        }
        if (data[8]) {
          this.marketplaces = data[8].data;
        }
        this.checkProjectModule();
      });
    },

    lockedFields(billing, automaticBilling) {
      if (automaticBilling === 'unlock') {
        this.formBasicInformation.enableField('automaticBilling');
        this.formBasicInformation.enableField('billingInterval');
        this.formBasicInformation.enableField('startBilling');
      }
      if (automaticBilling !== 'unlock') {
        this.formBasicInformation.disableField('automaticBilling');
        this.formBasicInformation.disableField('billingInterval');
        this.formBasicInformation.disableField('startBilling');
      }

      if (billing === 'unlock') {
        this.formBasicInformation.enableField('billingType');
      }
      if (billing !== 'unlock') {
        this.formBasicInformation.disableField('billingType');
      }
    },

    setCurrentContacts(contacts) {
      const currentContacts = [];
      contacts.forEach((contact) => {
        const data = contactTransformer.fetch(contact);
        data.contactId = contact.id;
        const contactTypes = [];
        contact.contact_types.forEach((type) => {
          if (type.name === 'Primary') data.type = 'primary';
          contactTypes.push(type.id.toString());
        });
        data.contactType = contactTypes;
        data.id = contactTypes.join();
        currentContacts.push(data);
      });
      store.dispatch('setCurrentClientContacts', currentContacts);
    },

    setCurrentAddresses(addresses) {
      this.addressTypes.forEach((type) => {
        let address = addresses.find(a => type.id === a.address_type.id) || {};
        if (address) {
          address = addressTransformer.fetch(address);
        }
        this.clientAddresses.push(Object.assign({}, { id: type.id, addressType: type.id, name: type.name }, address));
      });
    },

    buttonHandler() {
      this.formBasicInformation.loading = true;
      const organizationNumber = this.formBasicInformation.organizationNumber;
      this.formBasicInformation.disableField('organizationNumber');

      clientService.findBusiness(organizationNumber).then((data) => {
        this.formBasicInformation.loading = false;
        // console.log(data);
        this.formBasicInformation.enableField('organizationNumber');
        this.formBasicInformation.companyName = data.name;
        this.setModalContent(() => clientAddressBrreg);
        this.setModalSize('30%');
        this.setModalClass('third-party-result-addresses');
        this.setModalProps({
          orgNumberInput: organizationNumber,
          addressTypes: this.addressTypes,
          orgData: data,
          onSubmit: this.onBreggSubmit,
        });
        this.toggleModal(true);
      }).catch((errors) => {
        this.$message({
          message: this.$t('static.clients.create.brrig.warning'),
          type: 'error',
        });
        this.formBasicInformation.enableField('organizationNumber');
        this.formBasicInformation.loading = false;
      });
    },

    buttonHandler2() {
      const organizationNumber = this.formBasicInformation.organizationNumber;
      this.formBasicInformation.loading = true;
      this.formBasicInformation.disableField('organizationNumber');
      clientService.glnLookup(organizationNumber)
      .then((res) => {
        if (res.gln !== '' && res.xKoord !== '' && res.yKoord !== '') {
          this.formBasicInformation.setFieldValue('gln', res.gln);
          this.formBasicInformation.setFieldValue('glnXKoord', res.xKoord);
          this.formBasicInformation.setFieldValue('glnYKoord', res.yKoord);
          this.formBasicInformation.enableField('organizationNumber');
          this.formBasicInformation.loading = false;
          this.$message({
            message: this.$t('static.clients.edit.glnSuccess'),
            type: 'success',
          });
        } else {
          this.formBasicInformation.enableField('organizationNumber');
          this.formBasicInformation.setFieldValue('gln', '');
          this.formBasicInformation.setFieldValue('glnXKoord', '');
          this.formBasicInformation.setFieldValue('glnYKoord', '');
          this.formBasicInformation.loading = false;
          this.$message({
            message: this.$t('static.clients.edit.glnError'),
            type: 'error',
          });
        }
      }).catch((error) => {
        console.log(error);
        this.formBasicInformation.enableField('organizationNumber');
        this.formBasicInformation.setFieldValue('gln', '');
        this.formBasicInformation.setFieldValue('glnXKoord', '');
        this.formBasicInformation.setFieldValue('glnYKoord', '');
        this.formBasicInformation.loading = false;
        this.$message({
          message: this.$t('static.clients.edit.glnError'),
          type: 'error',
        });
      });
    },

    onBreggSubmit(data) {
      // const i = this.clientAddresses.findIndex(item => item.addressType === types);
      // this.clientAddresses[i].streetName = data.streetName;
      // this.clientAddresses[i].secondStreetName = data.secondStreetName;
      // this.clientAddresses[i].postalCode = data.postalCode;
      // this.clientAddresses[i].cityName = data.cityName;
      // this.clientAddresses[i].countryId = this.countries.items.find(item => item.name === 'Norway').id;
      // this.update();

      const postal = data.address_postal;
      if (postal) {
        if (postal.address_type_ids) {
          postal.address_type_ids.forEach((item) => {
            const i = this.clientAddresses.findIndex(item2 => item2.addressType === item);
            this.clientAddresses[i].streetName = postal.street_name;
            this.clientAddresses[i].secondStreetName = postal.second_street_name;
            this.clientAddresses[i].postalCode = postal.postal_code;
            this.clientAddresses[i].cityName = postal.city;
            this.clientAddresses[i].countryId = this.countries.items.find(item2 => item2.name === 'Norway').id;
          });
        }
      }

      const business = data.address_business;
      if (business) {
        if (business.address_type_ids) {
          business.address_type_ids.forEach((item) => {
            const i = this.clientAddresses.findIndex(item2 => item2.addressType === item);
            this.clientAddresses[i].streetName = business.street_name;
            this.clientAddresses[i].secondStreetName = business.second_street_name;
            this.clientAddresses[i].postalCode = business.postal_code;
            this.clientAddresses[i].cityName = business.city;
            this.clientAddresses[i].countryId = this.countries.items.find(item2 => item2.name === 'Norway').id;
          });
        }
      }

      const location = data.address_location;
      if (location) {
        if (location.address_type_ids) {
          location.address_type_ids.forEach((item) => {
            const i = this.clientAddresses.findIndex(item2 => item2.addressType === item);
            this.clientAddresses[i].streetName = location.street_name;
            this.clientAddresses[i].secondStreetName = location.second_street_name;
            this.clientAddresses[i].postalCode = location.postal_code;
            this.clientAddresses[i].cityName = location.city;
            this.clientAddresses[i].countryId = this.countries.items.find(item2 => item2.name === 'Norway').id;
          });
        }
      }

      this.update();
    },
    destroy() {
      this.$confirm(this.$t('static.clients.delete.confirm'), this.$t('static.clients.delete.title'), {
        confirmButtonText: this.$t('button.delete'),
        cancelButtonText: this.$t('button.cancel'),
        type: 'warning',
      }).then(() => {
        // delete goes here...
        clientService.destroy(this.$route.params.id)
          .then(() => {
            this.$message({
              message: this.$t('static.clients.delete.success'),
              type: 'success',
            });
            this.$router.push({
              name: 'clients.index',
            });
          })
          .catch((errors) => {
            if (errors.system_generated_error) {
              this.$message({
                message: errors.system_generated_error[0],
                type: 'error',
              });
            } else {
              this.$message({
                message: errors.message,
                type: 'error',
              });
            }
          });
      }).catch(() => { });
    },
    setContactFieldErrors(errors) {
      if ('contacts' in errors) {
        this.contactFormErrors.primary = true;
      } else {
        this.contactFormErrors.primary = false;
      }
    },
    clearAddressErrors() {
      this.addressTypes.forEach((t) => {
        this.addressFormErrors.fields[t.id] = [];
      });
    },
    setAddressFieldErrors(errors) {
      if ('addresses' in errors) {
        this.addressFormErrors.postal = true;
      } else {
        this.addressFormErrors.postal = false;
        this.clearAddressErrors();
      }
      Object.keys(errors).filter(key => key.indexOf('addresses.') !== -1).map((key) => {
        const [name, addressTypeId, field] = key.split('.');
        if (typeof errors[key][0] !== 'undefined') {
          if (typeof this.addressFormErrors.fields[addressTypeId] === 'undefined') {
            this.addressFormErrors.fields[addressTypeId] = [];
            this.addressFormErrors.fields[addressTypeId][field] = [];
          }
          const message = errors[key][0].replace(key, field);
          this.addressFormErrors.fields[addressTypeId][field] = [message];
        }
        return null;
      });
    },
    getAddressFieldErrors(addressTypeId) {
      if (addressTypeId in this.addressFormErrors.fields) {
        return addressTransformer.fetch(this.addressFormErrors.fields[addressTypeId]);
      }
      return null;
    },
    searchMethod(search, field) {
      return Promise.all([
        clientService.all(search, this.$route.params.id),
      ]);
    },
    addLicense() {
      if (!this.formBasicInformation.organizationNumber) {
        const message = this.$t('static.license.organizationNumberRequired');
        this.$message({
          showClose: true,
          message,
          customClass: 'error',
        });
      } else {
        this.setModalContent(() => addLicense);
        this.setModalSize('full');
        this.toggleModal(true);
        this.setModalProps({ orgNumber: this.currentClient.organizationNumber, clientId: this.currentClient.id });
      }
    },
    sendCatalogue() {
      this.setModalContent(() => sendCatalogue);
      this.setModalProps({
        clientId: this.currentClient.id,
      });
      this.toggleModal(true);
    },
  },

  components: {
    VForm: require('./../../../../components/form/form.vue'),
    VMap: require('./../../../../components/map/map.vue'),
    VClientAddress: require('./../../../../components/clientAddress/clientAddress.vue'),
    VClientContact: require('./../../../../components/clientContact/clientContact.vue'),
  },

  mixins: [
    stringFormatMixin,
  ],
};



// WEBPACK FOOTER //
// ./src/app/pages/withNavigation/clients/edit/edit.js